import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Modal,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { observer } from "mobx-react";
import useStore from "../../../helpers/useStore";
import { useMasterTables } from "../../../stores/MasterTables";
import { TherapyDtoForUser, UserDto } from "../../../api-client";
import { RxStoreContext } from "../../../stores/Rx/rx.provider";
import { useUserByIdWithTherapies } from "../../../stores/User/user.queries";
import { useSaveUserInfo } from "../../../stores/User/user.mutations";

const EXCEPTION_IDS = new Set(["Privat_MT", "Privat_KGN BO", "Privat_KGN VOJ", "Privat_KGN PNF"]);

const CATEGORY_EXCEPTIONS: Record<string, string[]> = {
  "Manuelle Therapie": ["Privat_MT"],
  "KG ZNS PNF": ["Privat_KGN PNF"],
  "KG ZNS Vojta Adults": ["Privat_KGN VOJ"],
  "KG ZNS Bobath Adults": ["Privat_KGN BO"],
};

interface TherapiesAssignmentProps {
  userData: any;
  onSelectUser: (user: any) => void;
}
/** The line item as this screen consumes it — the generated shape, unaltered. */
type Therapy = TherapyDtoForUser;

/**
 * The key a checkbox in this screen toggles (MD-232).
 *
 * Grants are per LINE ITEM, and a line item is (method x tarif group x
 * discipline). Grouping by bare abbreviation merged the disciplines — ticking
 * "HB" granted both GKV_HB and GKV_ergo_HB — so the key carries the discipline
 * and prefers the method id, which is the tarif-agnostic identity now that
 * abbreviation is a display label.
 *
 * The Privat exceptions stay keyed by their own id: private MT is grantable
 * without the MT training, and that per-tarif-group split is the whole reason
 * credentials attach at line-item level rather than to the method.
 */
const groupKeyOf = (t: Therapy): string =>
  EXCEPTION_IDS.has(t.id)
    ? t.id
    : `${t.therapyType ?? "ANY"}|${t.therapyMethodId || t.abbreviation}`;
interface Category {
  id: string;
  displayName: string;
  therapies: Therapy[];
}


const TherapiesAssignment: React.FC<TherapiesAssignmentProps> = observer(
  ({ userData, onSelectUser }) => {
    const { data: masterTablesData } = useMasterTables();
    const saveUserInfoMutation = useSaveUserInfo();
    const [formState, setFormState] = useState<UserDto>(userData);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const RxStore = useStore(RxStoreContext);
    const [categories, setCategories] = useState<Category[]>([]);

    const { data: userWithTherapies } = useUserByIdWithTherapies(userData?.id);

    // `handleSubmit` PUTs the whole `formState` back, so saving before the
    // therapy-bearing record has arrived would persist the therapy-less
    // fallback and wipe the user's certifications. New users (no id) have
    // nothing to wait for.
    const isRecordLoaded = !userData?.id || !!userWithTherapies;

    // Keyed on the user id, not the `userData` object: the parent rebuilds that
    // object on data refreshes, and re-syncing on identity alone would discard
    // the admin's unsaved selections.
    useEffect(() => {
      setFormState(userWithTherapies ?? userData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userWithTherapies, userData?.id]);

    useEffect(() => {
      const fetchCertifications = async () => {
        try {
          const certs: Category[] = await RxStore.getAllTherapyCertifications();
          setCategories(certs);
        } catch (err) {
        }
      };

      fetchCertifications();
    }, [RxStore]);

    // extract the therapy array from masterTablesData
    let therapyOptions: Therapy[] = [];
    try {
      const therapyArrays: Therapy[][] =
        masterTablesData?.filter((obj: Therapy) => "therapy" in obj)?.map((obj: any) => obj.therapy) ||
        [];
      therapyOptions = therapyArrays[0] || [];
    } catch (e) {
      console.warn("Could not derive therapyOptions from masterTablesData", e);
    }

    const groupMap: Record<string, Therapy[]> = useMemo(() => {
      const map: Record<string, Therapy[]> = {};
      (therapyOptions || []).forEach((t) => {
        if (t.duration == null) return;
        if (!EXCEPTION_IDS.has(t.id) && !(t.therapyMethodId || (t.abbreviation || "").trim())) return;
        const key = groupKeyOf(t);
        if (!map[key]) map[key] = [];
        map[key].push(t);
      });
      return map;
    }, [therapyOptions]);

    // ---- Handle checkbox toggle per group (method x discipline) ----
    const handleGroupToggle = (key: string) => {
      const therapies: Therapy[] = groupMap[key] || [];
      if (!therapies.length) return;

      const allSelected = therapies.every((t: Therapy) =>
        formState.therapies?.some((ut: any) => ut.id === t.id)
      );

      let updatedTherapies;
      if (allSelected) {
        updatedTherapies = formState.therapies?.filter(
          (ut: any) => !therapies.some((t: any) => t.id === ut.id)
        );
      } else {
        const missing = therapies.filter(
          (t: Therapy) => !formState.therapies?.some((ut: any) => ut.id === t.id)
        );
        updatedTherapies = [...(formState.therapies || []), ...missing];
      }

      setFormState({ ...formState, therapies: updatedTherapies });
    };

    // Category (certification) toggles operate on the certification's FULL
    // member list, NOT on what the grid happens to render. groupMap exists for
    // display and drops rows without a duration (PostBlanko_BD/PD have none),
    // so a groupMap-based uncheck silently left those grants behind — a
    // therapist stripped of "Alle Physiotherapeuten" kept PostBlanko rows
    // forever. Granting is made symmetric for the same reason: checking a
    // certification grants every row it owns, exactly like the server-side
    // defaults for a new therapist.
    const isCategoryChecked = (members: Therapy[]) => {
      if (!formState.therapies?.length) return false;
      if (members.length === 0) return false;

      return members.every((t: Therapy) =>
        formState.therapies?.some((ut: any) => ut.id === t.id)
      );
    };

    // Handle category toggle
    const handleCategoryToggle = (members: Therapy[]) => {
      const allChecked = isCategoryChecked(members);
      let updatedTherapies;
      if (allChecked) {
        updatedTherapies = (formState.therapies || []).filter(
          (ut: any) => !members.some((t: Therapy) => t.id === ut.id)
        );
      } else {
        const currentTherapies = formState.therapies || [];
        const currentTherapyIds = new Set(currentTherapies.map((t: any) => t.id));
        const therapiesToAdd = members.filter((t) => !currentTherapyIds.has(t.id));
        updatedTherapies = [...currentTherapies, ...therapiesToAdd];
      }

      setFormState({ ...formState, therapies: updatedTherapies });
    };

    const handleSubmit = async () => {
      if (!isRecordLoaded) return;
      try {
        setLoading(true);
        const response = await saveUserInfoMutation.mutateAsync(formState);
        onSelectUser(response);
      } catch (error) {
        console.error("API Error:", error);
      } finally {
        setLoading(false);
      }
    };

    const processedCategories = categories.map((cat) => {
      const map: Record<string, { label: string; description?: string; therapyType?: string | null }> = {};

      // The full row set this category's checkbox governs: every therapy the
      // certification owns (whether or not the grid renders it) minus the
      // Privat exceptions that are re-homed under other categories, plus the
      // exceptions re-homed HERE.
      const memberTherapies: Therapy[] = cat.therapies.filter(
        (t: Therapy) => !EXCEPTION_IDS.has(t.id)
      );

      // First, process regular therapies
      cat.therapies.forEach((t: Therapy) => {
        if (EXCEPTION_IDS.has(t.id)) return;
        const key = groupKeyOf(t);
        if (!map[key]) {
          map[key] = { label: t.abbreviation, description: t.description, therapyType: t.therapyType };
        }
      });

      // Then, check if this category should have any exceptions
      const exceptionsForThisCategory = CATEGORY_EXCEPTIONS[cat.displayName] || [];
      exceptionsForThisCategory.forEach((exceptionId) => {
        // Find the exception therapy in all categories
        const exceptionTherapy = categories
          .flatMap((c) => c.therapies)
          .find((t) => t.id === exceptionId);
        if (exceptionTherapy) {
          memberTherapies.push(exceptionTherapy);
          map[exceptionId] = {
            label: exceptionTherapy.abbreviation || exceptionId,
            description: exceptionTherapy.description,
            therapyType: exceptionTherapy.therapyType,
          };
        }
      });

      return {
        id: cat.id,
        displayName: cat.displayName,
        memberTherapies,
        therapies: Object.entries(map).map(([key, value]) => ({
          key,
          label: value.label,
          description: value.description,
          therapyType: value.therapyType,
        })),
      };
    });

    return (
      <Box
        sx={{
          width: "100%",
          height: "calc(100vh - 200px)",
          bgcolor: "background.paper",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            pb: "80px",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "8px",
              "&:hover": {
                background: "#666",
              },
            },
          }}
        >
          <Typography variant="h5" sx={{ p: 2, pb: 1 }}>
            Fortbildungen und Heilmitteln
          </Typography>

          {processedCategories.map((cat) => (
            <List
              key={cat.id}
              sx={{
                bgcolor: "background.paper",
                m: 0,
                p: 0,
                "& .MuiListItem-root": {
                  m: 0,
                  p: 0,
                },
              }}
            >
              <ListItem
                disablePadding
                sx={{
                  py: 0,
                  "& .MuiListItemText-root": {
                    margin: 0,
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "left",
                        justifyContent: "left",
                        py: 2,
                        width: "100%",
                        paddingBottom: 0,
                        paddingTop: 0,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "left", flex: 1, position: "relative" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "left",
                            justifyContent: "space-between",
                            right: 0,
                          }}
                        >
                          <IconButton
                            onClick={() => {
                              setExpandedCategories((prev) => {
                                const newSet = new Set(prev);
                                if (newSet.has(cat.displayName)) {
                                  newSet.delete(cat.displayName);
                                } else {
                                  newSet.add(cat.displayName);
                                }
                                return newSet;
                              });
                            }}
                            size="small"
                          >
                            {expandedCategories.has(cat.displayName) ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "left",
                            width: "100%",
                            marginLeft: 1,
                          }}
                        >
                          <Checkbox
                            edge="start"
                            checked={isCategoryChecked(cat.memberTherapies)}
                            onChange={() => handleCategoryToggle(cat.memberTherapies)}
                            disableRipple
                          />
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, ml: 0 }}>
                            {cat.displayName}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Collapse
                      in={expandedCategories.has(cat.displayName)}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                          gap: 1,
                          pl: 2,
                          mt: 1,
                        }}
                      >
                        {cat.therapies.map((t) => {
                          const therapiesForGroup = groupMap[t.key] || [];
                          const selectedCount = therapiesForGroup.filter((ther) =>
                            ((formState.therapies || []) as Therapy[]).some(
                              (ut) => ut.id === ther.id
                            )
                          ).length;
                          const total = therapiesForGroup.length;
                          const showIndividual = total > 1; // Show children if multiple variants exist

                          return (
                            <Box key={t.key} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                              {/* Parent therapy item */}
                              <ListItemButton onClick={() => handleGroupToggle(t.key)} dense>
                                <ListItemIcon>
                                  {(() => {
                                    if (selectedCount === 0) {
                                      return <CheckBoxOutlineBlankIcon fontSize="medium" sx={{ color: "#666" }} />;
                                    }
                                    if (selectedCount === total) {
                                      return <CheckBoxIcon fontSize="medium" sx={{ color: "#1976d2" }} />;
                                    }
                                    return <IndeterminateCheckBoxIcon fontSize="medium" sx={{ color: "#1976d2" }} />;
                                  })()}
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <React.Fragment>
                                      <Typography component="span" sx={{ fontWeight: "bold" }}>
                                        {t.label}
                                      </Typography>
                                      {t.therapyType && (
                                        <Typography
                                          component="span"
                                          sx={{ fontWeight: "normal", fontSize: "0.75rem", ml: 1, opacity: 0.7 }}
                                        >
                                          {t.therapyType}
                                        </Typography>
                                      )}
                                      {t.description && (
                                        <Typography component="span" sx={{ fontWeight: "normal" }}>
                                          {` (${t.description})`}
                                        </Typography>
                                      )}
                                      {showIndividual && (
                                        <Typography component="span" sx={{ fontWeight: "normal", fontSize: "0.875rem", ml: 1 }}>
                                          ({selectedCount}/{total})
                                        </Typography>
                                      )}
                                    </React.Fragment>
                                  }
                                />
                              </ListItemButton>

                              {/* Individual therapy variants */}
                              {showIndividual && (
                                <Box sx={{ pl: 4, pb: 1, bgcolor: "rgba(0,0,0,0.02)" }}>
                                  {therapiesForGroup.map((therapy) => {
                                    const isSelected = formState.therapies?.some((ut: any) => ut.id === therapy.id);
                                    return (
                                      <Box
                                        key={therapy.id}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          cursor: "pointer",
                                          py: 0.5,
                                          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const updatedTherapies = isSelected
                                            ? formState.therapies?.filter((ut: any) => ut.id !== therapy.id)
                                            : [...(formState.therapies || []), therapy];
                                          setFormState({ ...formState, therapies: updatedTherapies });
                                        }}
                                      >
                                        {isSelected
                                          ? <CheckBoxIcon fontSize="small" sx={{ color: "#1976d2" }} />
                                          : <CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: "#666" }} />
                                        }
                                        <Typography variant="body2" sx={{ ml: 1 }}>
                                          {therapy.id}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  }
                />
              </ListItem>
            </List>
          ))}
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "16px",
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Button
            color="secondary"
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !isRecordLoaded}
          >
            Speichern
          </Button>
        </Box>

        <Modal
          open={loading}
          aria-labelledby="loading-modal-title"
          aria-describedby="loading-modal-description"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div>
            <CircularProgress size={60} />
          </div>
        </Modal>
      </Box>
    );
  }
);

export default TherapiesAssignment;
